// Client-side mirror of the context-free part of backend/src/lib/
// passwordPolicy.js — just for immediate typing feedback. The backend
// check is the real enforcement (also catches username/mobile-number
// similarity, which needs context this shared component doesn't always
// have) — this only needs to stay loosely in sync, not exactly.

const MIN_LENGTH = 8;

// Kept in sync with backend/src/lib/passwordPolicy.js — also used for
// the requirements hint text shown under the field (not just enforced).
export const SPECIAL_CHARS_DISPLAY = '@ $ ! % * ? & # ^ / ~';
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/;

const BLOCKED_BASES = [
  'bescom', 'password', 'passw0rd', 'admin', 'welcome', 'qwerty',
  'letmein', 'changeme', 'default', '12345678', 'iloveyou',
];

function normalize(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

// `context.username` / `context.phone` are optional — every caller that
// doesn't know them (add-phone, reset-password, etc.) just omits them and
// gets exactly the old behavior. Mirrors backend validatePasswordStrength's
// username/mobile-number rules so registration can say so BEFORE submit.
export function checkPasswordStrength(password, context = {}) {
  const pw = String(password || '');
  if (!pw) return null; // let `required` handle the empty case

  if (pw.length < MIN_LENGTH) {
    return `Password must be at least ${MIN_LENGTH} characters.`;
  }
  if (!/[a-zA-Z]/.test(pw) || !/[0-9]/.test(pw)) {
    return 'Password must contain both letters and numbers.';
  }
  if (!SPECIAL_CHARS_REGEX.test(pw)) {
    return `Password must contain at least one special character (${SPECIAL_CHARS_DISPLAY}).`;
  }
  const normalizedPw = normalize(pw);
  if (BLOCKED_BASES.some((base) => normalizedPw.includes(base))) {
    return 'Password is too common or guessable. Please choose a different one.';
  }
  if (context.username) {
    const normalizedUser = normalize(context.username);
    if (normalizedUser.length >= 3 && normalizedPw.includes(normalizedUser)) {
      return 'Password must not contain your username.';
    }
  }
  if (context.phone) {
    const phoneTail = digitsOnly(context.phone).slice(-10);
    if (phoneTail.length === 10 && digitsOnly(pw).includes(phoneTail)) {
      return 'Password must not contain your mobile number.';
    }
  }
  return null;
}
